/**
 * classifyTutorial · 分类新手教程的跳转入口 + 教程 banner 展示缓存
 *
 * 教程 banner（对话滚动区顶部）展示规则 = (① 或 ②) 且 当天未被关闭：
 *  ① 当前用户没有通过 AI 助手批量创建过文件夹 —— 批量建夹确认成功时写用户级标记
 *    （useConfirmCreate 调 markBatchCreatedFolders）
 *  ② 团队文件夹用量为 0 —— 取服务端接口4 的 quota.used（folderQuotaSlice）
 * 关闭规则：点 X 按「用户 + 自然日」记录，当天不再展示、次日自动恢复。
 *
 * 单层缓存：两条记录同为用户维度，合并存一个 localStorage 条目
 *  ai_classify_tutorial__<userID> = {"batchCreatedAt":"YYYY-MM-DD","dismissedAt":"YYYY-MM-DD"}
 *  · batchCreatedAt 存在即视为"批量建过夹"（值仅留档）
 *  · dismissedAt == 今天 → 当天不再展示
 */

import desktopLib from '../../../utils/desktopLib'

/** 分类新手教程静态页（CDN 托管，内容独立于前端发版迭代） */
export const CLASSIFY_TUTORIAL_URL =
  'https://static.xhey.top/work/ai-classify-tutorial.html'

/**
 * 打开分类新手教程 · banner 与 ChatFeed 空态「怎么用 AI 批量分类？」共用入口，
 * 教程 URL 只维护在 CLASSIFY_TUTORIAL_URL 一处（空值守卫留作下线开关）。
 *
 * 桌面客户端（Electron）里 window.open 会开应用内窗口，改走 desktopLib.openContact
 * 让系统浏览器打开（与 RemoveWatermarkModal 等既有外链入口同款分支）。
 */
export function openClassifyTutorial(): void {
  if (!CLASSIFY_TUTORIAL_URL) return
  if (desktopLib.isDesktop) {
    desktopLib.openContact(CLASSIFY_TUTORIAL_URL)
  } else {
    window.open(CLASSIFY_TUTORIAL_URL, '_blank', 'noopener')
  }
}

const TUTORIAL_STATE_KEY_PREFIX = 'ai_classify_tutorial__'

/** 单条目缓存的载荷 · 字段均可缺省，坏 JSON / 异常一律按空状态处理 */
interface TutorialState {
  /** 该用户首次批量建夹日期 · 存在即算"批量建过" */
  batchCreatedAt?: string
  /** 该用户最近一次关闭 banner 的日期 · 等于今天则当天不再展示 */
  dismissedAt?: string
}

/**
 * 本地时区日期串 YYYY-MM-DD · 关闭层按"自然日"恢复展示，跨天即失效。
 *
 * @param now - 参考时间，默认当前时刻（测试可注入固定日期）
 * @returns 形如 "2026-07-06"
 *
 * @example
 * ```ts
 * formatDateKey(new Date(2026, 6, 6)) // "2026-07-06"
 * ```
 */
export function formatDateKey(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

/** 读该用户的教程缓存条目 · 无记录 / 坏 JSON / 隐私模式异常 → 空状态 */
function readTutorialState(userID: string): TutorialState {
  try {
    const raw = window.localStorage.getItem(TUTORIAL_STATE_KEY_PREFIX + userID)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

/** 合并写该用户的教程缓存条目 · 只覆盖给到的字段，保留另一字段；写失败静默 */
function patchTutorialState(userID: string, patch: TutorialState): void {
  try {
    const next = { ...readTutorialState(userID), ...patch }
    window.localStorage.setItem(
      TUTORIAL_STATE_KEY_PREFIX + userID,
      JSON.stringify(next)
    )
  } catch {
    // 忽略：隐私模式 / 配额满时退化为"不记忆"（banner 多展示一次无害）
  }
}

/** 当前用户是否已通过 AI 助手批量创建过文件夹（条件①取反） */
export function hasBatchCreatedFolders(userID: string | undefined): boolean {
  if (!userID) return false
  return readTutorialState(userID).batchCreatedAt !== undefined
}

/** 记录当前用户已批量创建过文件夹 · 批量建夹确认成功时调用 */
export function markBatchCreatedFolders(userID: string | undefined): void {
  if (!userID) return
  patchTutorialState(userID, { batchCreatedAt: formatDateKey() })
}

/** 该用户今天是否关闭过教程 banner */
export function isTutorialDismissedToday(
  userID: string | undefined,
  now: Date = new Date()
): boolean {
  if (!userID) return false
  return readTutorialState(userID).dismissedAt === formatDateKey(now)
}

/** 记录该用户今天关闭了教程 banner · 次日 formatDateKey 变化自动失效 */
export function dismissTutorialToday(
  userID: string | undefined,
  now: Date = new Date()
): void {
  if (!userID) return
  patchTutorialState(userID, { dismissedAt: formatDateKey(now) })
}

export interface TutorialBannerConditions {
  /** 当前用户是否已批量建过夹（hasBatchCreatedFolders） */
  batchCreated: boolean
  /** 配额是否已就绪（loaded 且属于当前团队） */
  quotaLoaded: boolean
  /** 配额接口是否失败（失败时条件②不可判定，按不满足处理） */
  quotaError: boolean
  /** 服务端下发的团队文件夹用量 */
  quotaUsed: number
  /** 该用户今天是否关闭过 banner */
  dismissedToday: boolean
}

/**
 * 教程 banner 展示判定（纯函数）
 *
 * 规则：当天关闭过 → 一票否决；否则 ①该用户未批量建过夹 或 ②团队用量=0
 * （须配额就绪且未失败）任一满足即展示。条件①不依赖配额，配额未就绪时仅靠①判定。
 *
 * @param conditions - 各输入含义见 TutorialBannerConditions 字段注释
 * @returns 是否展示 banner
 *
 * @example
 * ```ts
 * shouldShowTutorialBanner({ batchCreated: false, quotaLoaded: false, quotaError: false, quotaUsed: 0, dismissedToday: false }) // true（条件①）
 * ```
 */
export function shouldShowTutorialBanner(
  conditions: TutorialBannerConditions
): boolean {
  const { batchCreated, quotaLoaded, quotaError, quotaUsed, dismissedToday } =
    conditions
  if (dismissedToday) return false
  const neverBatchCreated = !batchCreated
  const folderCountZero = quotaLoaded && !quotaError && quotaUsed === 0
  return neverBatchCreated || folderCountZero
}
